import * as React from "react";
import {
  PERSONA_SCRIPT_IMPORT_MAX_BYTES,
  PERSONA_SCRIPT_IMPORT_MAX_CHARS,
  PERSONA_SCRIPT_IMPORT_TITLE_MAX_CHARS,
} from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  SegmentedControl,
  Textarea,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { getLocalUserFacingApiMessage } from "@/lib/api-client";
import {
  useExtractPersonaScriptImport,
  useImportPersonaBriefFromText,
  useImportPersonaScriptFromText,
} from "@/lib/queries/persona-opportunity-scripts.queries";
import { amberFailureToastMessage } from "@/lib/use-amber-mutation";

import { PersonaImportFileZone } from "./PersonaImportFileZone.js";
import { mergeImportText } from "./merge-import-text.js";
import { HubScriptSetPicker, type HubScriptSetPickerProps } from "../../shared/HubScriptSetPicker/HubScriptSetPicker.js";
import { isSelectableHubScriptSetId } from "../../../lib/hub-script-target.js";

const IMPORT_MAX_MB = PERSONA_SCRIPT_IMPORT_MAX_BYTES / (1024 * 1024);
const IMPORT_DIALOG_HEIGHT_CLASS = "h-[min(90vh,48rem)]";
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export type ImportKind = "script" | "brief";

export interface PersonaImportScriptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  tenantId: string;
  /** Hub: pick or create the target set inside this dialog. Omitted in the single-set workspace. */
  setPicker?: HubScriptSetPickerProps;
  /** Keep this dialog open while the create-set dialog is on top. */
  suspendDismiss?: boolean;
  /** When set, the parent owns the script/brief switch. */
  kind?: ImportKind;
  onKindChange?: (kind: ImportKind) => void;
  /** Render the form without a dialog shell. */
  embedded?: boolean;
}

function isLegacyDoc(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === "application/msword" || (name.endsWith(".doc") && !name.endsWith(".docx"));
}

function isAllowedImportFile(file: File): boolean {
  if (isLegacyDoc(file)) return false;
  const name = file.name.toLowerCase();
  return (
    file.type === "text/plain" ||
    name.endsWith(".txt") ||
    file.type === "application/pdf" ||
    name.endsWith(".pdf") ||
    file.type === DOCX_MIME ||
    name.endsWith(".docx")
  );
}

export function PersonaImportScriptModal({
  open,
  onOpenChange,
  setId,
  tenantId,
  setPicker,
  suspendDismiss = false,
  kind: controlledKind,
  onKindChange,
  embedded = false,
}: PersonaImportScriptModalProps) {
  const { t } = useTranslation();
  const extractRunIdRef = React.useRef(0);
  const textRef = React.useRef("");
  const [uncontrolledKind, setUncontrolledKind] = React.useState<ImportKind>("script");
  const kind = controlledKind ?? uncontrolledKind;
  const [title, setTitle] = React.useState("");
  const [text, setText] = React.useState("");
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const acceptedRef = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const extractImport = useExtractPersonaScriptImport();
  const importScript = useImportPersonaScriptFromText({
    onAccepted: () => {
      acceptedRef.current = true;
      setIsSubmitting(false);
      onOpenChange(false);
    },
  });
  const importBrief = useImportPersonaBriefFromText({
    onAccepted: () => {
      acceptedRef.current = true;
      setIsSubmitting(false);
      onOpenChange(false);
    },
  });

  const isImporting = isSubmitting;
  const busy = extractImport.isPending || isImporting;
  const confirmDisabled = busy || !isSelectableHubScriptSetId(setId) || !text.trim();

  React.useEffect(() => {
    if (!open) {
      extractRunIdRef.current += 1;
      return;
    }
    acceptedRef.current = false;
    textRef.current = "";
    setTitle("");
    setText("");
    setSelectedFile(null);
    if (controlledKind == null) setUncontrolledKind("script");
    setError(null);
    setIsSubmitting(false);
  }, [open, controlledKind]);

  const handleOpenChange = (next: boolean) => {
    if (!next && (busy || suspendDismiss)) return;
    onOpenChange(next);
  };

  const setKind = (next: ImportKind) => {
    onKindChange?.(next);
    if (controlledKind == null) setUncontrolledKind(next);
    setError(null);
  };

  const handleFiles = (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    if (list.length > 1) {
      setError(
        t("personaOpportunities.scripts.import.tooManyFiles", "Upload one file at a time."),
      );
      return;
    }
    void handleFile(list[0]);
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (isLegacyDoc(file)) {
      setSelectedFile(null);
      setError(
        t(
          "personaOpportunities.scripts.import.legacyDoc",
          "Legacy .doc files are not supported — convert to DOCX first.",
        ),
      );
      return;
    }
    if (!isAllowedImportFile(file)) {
      setSelectedFile(null);
      setError(
        t("personaOpportunities.scripts.import.invalidType", "Upload a PDF, DOCX, or TXT file."),
      );
      return;
    }
    if (file.size > PERSONA_SCRIPT_IMPORT_MAX_BYTES) {
      setSelectedFile(null);
      setError(
        t("personaOpportunities.scripts.import.tooLarge", {
          maxMb: IMPORT_MAX_MB,
          defaultValue: "File must be {{maxMb}}MB or smaller.",
        }),
      );
      return;
    }
    setSelectedFile(file);
    const runId = ++extractRunIdRef.current;
    try {
      const extracted = await extractImport.mutateAsync({ setId, tenantId, file });
      if (extractRunIdRef.current !== runId) return;
      const merged = mergeImportText(textRef.current, extracted.text, PERSONA_SCRIPT_IMPORT_MAX_CHARS);
      if (!merged.ok) {
        setError(
          t("personaOpportunities.scripts.import.overCap", {
            max: PERSONA_SCRIPT_IMPORT_MAX_CHARS.toLocaleString(),
            defaultValue: "Import text cannot exceed {{max}} characters.",
          }),
        );
        return;
      }
      textRef.current = merged.text;
      setText(merged.text);
    } catch (err) {
      if (extractRunIdRef.current !== runId) return;
      setError(
        getLocalUserFacingApiMessage(err) ??
          t(
            "personaOpportunities.scripts.import.extractFailed",
            "Could not read that file. Try another PDF, DOCX, or TXT.",
          ),
      );
    }
  };

  const handleConfirm = () => {
    const trimmed = text.trim();
    if (!trimmed || busy || !isSelectableHubScriptSetId(setId)) return;
    setError(null);
    setIsSubmitting(true);
    const mutate = kind === "brief" ? importBrief : importScript;
    mutate.mutate(
      { setId, tenantId, text: trimmed, title: title.trim() || undefined },
      {
        onError: (err) => {
          setIsSubmitting(false);
          if (acceptedRef.current) return;
          setError(
            amberFailureToastMessage(err) ??
              t(
                kind === "brief"
                  ? "personaOpportunities.scripts.import.briefFailed"
                  : "personaOpportunities.scripts.import.failed",
                kind === "brief"
                  ? "Could not import that brief. Try again."
                  : "Could not import that script. Try again.",
              ),
          );
        },
      },
    );
  };

  const confirmLabel = isImporting
    ? t("personaOpportunities.scripts.import.importing", "Importing…")
    : kind === "brief"
      ? t("personaOpportunities.scripts.import.briefConfirm", "Add brief")
      : t("personaOpportunities.scripts.import.confirm", "Import script");

  const form = (
    <>
      {embedded ? null : (
        <DialogHeader className="shrink-0 gap-1 border-b border-border py-4 pr-14 pl-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <DialogTitle>
                {t("personaOpportunities.scripts.import.title", "Import")}
              </DialogTitle>
              <DialogDescription>
                {kind === "brief"
                  ? t(
                      "personaOpportunities.scripts.import.descriptionBriefText",
                      "Paste a brief, or upload one file. We'll format it into a brief record.",
                    )
                  : t(
                      "personaOpportunities.scripts.import.description",
                      "Paste a script, or upload one file. Review the text below, then confirm.",
                    )}
              </DialogDescription>
            </div>
            {controlledKind == null ? (
              <SegmentedControl
                value={kind}
                disabled={busy}
                accent="primary"
                tone="subtle"
                className="shrink-0"
                ariaLabel={t("personaOpportunities.scripts.import.kindAria", "What to import")}
                onValueChange={(value) => {
                  if (value === "script" || value === "brief") setKind(value);
                }}
                options={[
                  {
                    value: "script",
                    label: t("personaOpportunities.scripts.import.kindScript", "Script"),
                  },
                  {
                    value: "brief",
                    label: t("personaOpportunities.scripts.import.kindBrief", "Brief"),
                  },
                ]}
              />
            ) : null}
          </div>
        </DialogHeader>
      )}

      {setPicker && !embedded ? <HubScriptSetPicker {...setPicker} /> : null}

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-6 py-5">
        <div className="flex shrink-0 flex-col gap-1.5">
          <label htmlFor="persona-import-title" className="text-xs font-medium text-foreground">
            {t("personaOpportunities.scripts.import.titleLabel", "Title (optional)")}
          </label>
          <Input
            id="persona-import-title"
            data-testid="persona-import-title"
            value={title}
            maxLength={PERSONA_SCRIPT_IMPORT_TITLE_MAX_CHARS}
            disabled={busy}
            placeholder={t(
              "personaOpportunities.scripts.import.titlePlaceholder",
              "Leave blank to name it from the text",
            )}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <PersonaImportFileZone
          file={selectedFile}
          isExtracting={extractImport.isPending}
          disabled={busy}
          onFiles={handleFiles}
        />
        <div className="flex min-h-0 flex-1 flex-col gap-1.5">
          <div className="flex shrink-0 items-baseline justify-between gap-3">
            <label htmlFor="persona-import-script-text" className="text-xs font-medium text-foreground">
              {selectedFile
                ? t(
                    "personaOpportunities.scripts.import.reviewHint",
                    "Review and edit before confirming",
                  )
                : kind === "brief"
                  ? t("personaOpportunities.scripts.import.briefLabel", "Brief")
                  : t("personaOpportunities.scripts.import.scriptLabel", "Script")}
            </label>
            <p className="text-xs text-muted-foreground" aria-live="polite">
              {t("personaOpportunities.scripts.import.charCount", {
                count: text.length,
                max: PERSONA_SCRIPT_IMPORT_MAX_CHARS.toLocaleString(),
                defaultValue: "{{count}} / {{max}} characters",
              })}
            </p>
          </div>
          <Textarea
            id="persona-import-script-text"
            data-testid="persona-import-script-text"
            value={text}
            maxLength={PERSONA_SCRIPT_IMPORT_MAX_CHARS}
            aria-label={
              kind === "brief"
                ? t("personaOpportunities.scripts.import.briefPlaceholder", "Paste your brief…")
                : t("personaOpportunities.scripts.import.placeholder", "Paste your script…")
            }
            onChange={(event) => {
              textRef.current = event.target.value;
              setText(event.target.value);
            }}
            disabled={busy}
            placeholder={
              kind === "brief"
                ? t("personaOpportunities.scripts.import.briefPlaceholder", "Paste your brief…")
                : t("personaOpportunities.scripts.import.placeholder", "Paste your script…")
            }
            className="min-h-[16rem] flex-1 resize-none text-[13px] leading-relaxed"
          />
        </div>
        {isImporting ? (
          <p role="status" className="sr-only">
            {t("personaOpportunities.scripts.import.importing", "Importing…")}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
            <Icon name="alert-circle" size="xs" color="inherit" className="shrink-0" />
            {error}
          </p>
        ) : null}
      </div>

      {embedded ? (
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/60 px-6 py-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="persona-import-confirm"
            className={cn(
              isImporting && "[&_svg]:motion-safe:animate-spin [&_svg]:motion-reduce:animate-none",
            )}
            disabled={confirmDisabled}
            iconStart={isImporting ? "loader" : "upload"}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      ) : (
        <DialogFooter className="!mx-0 !mb-0 flex flex-wrap items-center justify-between gap-4 !bg-muted !px-6 py-3 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            className="h-[42px] px-3.5"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            data-testid="persona-import-confirm"
            className={cn(
              "h-[42px] px-5 font-semibold",
              isImporting && "[&_svg]:motion-safe:animate-spin [&_svg]:motion-reduce:animate-none",
            )}
            disabled={confirmDisabled}
            iconStart={isImporting ? "loader" : undefined}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      )}
    </>
  );

  if (embedded) {
    return open ? form : null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        variant="form"
        className={cn(
          "grid gap-0 overflow-hidden p-0",
          setPicker
            ? "grid-rows-[auto_auto_minmax(0,1fr)_auto]"
            : "grid-rows-[auto_minmax(0,1fr)_auto]",
          IMPORT_DIALOG_HEIGHT_CLASS,
        )}
        data-testid="persona-import-script-modal"
        onInteractOutside={(event) => {
          if (suspendDismiss) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (suspendDismiss) event.preventDefault();
        }}
        onFocusOutside={(event) => {
          if (suspendDismiss) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (suspendDismiss) event.preventDefault();
        }}
      >
        {form}
      </DialogContent>
    </Dialog>
  );
}
